import React, { useState, useEffect } from 'react';
import { 
    Box, Grid, Card, CardContent, Typography, Button, Divider, 
    FormControlLabel, Switch, Alert
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';

import API_BASE_URL from '../config/api';

export default function RolePermissionsView() {
    const { tenantSubdomain, showToast } = useApp();
    const { t } = useLanguage();

    const [permissionsData, setPermissionsData] = useState([]);
    const [selectedRole, setSelectedRole] = useState('manager');
    const [allowedMenusList, setAllowedMenusList] = useState([]);
    const [lockedActionsList, setLockedActionsList] = useState([]);
    const [saveLoading, setSaveLoading] = useState(false);

    const fetchPermissions = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/tenant/permissions`, {
                headers: { 'X-Tenant-Subdomain': tenantSubdomain }
            });
            const data = await response.json();
            if (data.success) {
                setPermissionsData(data.permissions || []);
            } else {
                showToast(data.error || 'Failed to fetch permissions');
            }
        } catch (err) {
            console.error("Failed to load permissions:", err);
            showToast("Network error while loading permissions");
        }
    };

    useEffect(() => {
        if (tenantSubdomain) {
            fetchPermissions();
        }
    }, [tenantSubdomain]);

    useEffect(() => {
        const rolePerm = permissionsData.find(p => p.role === selectedRole);
        if (rolePerm) {
            setAllowedMenusList(rolePerm.allowed_menus || []);
            setLockedActionsList(rolePerm.locked_actions || []);
        } else {
            setAllowedMenusList([]);
            setLockedActionsList([]);
        }
    }, [selectedRole, permissionsData]);

    const handleToggleMenu = (menuId) => {
        setAllowedMenusList(prev => 
            prev.includes(menuId) ? prev.filter(m => m !== menuId) : [...prev, menuId]
        );
    };

    const handleToggleAction = (actionId) => {
        setLockedActionsList(prev => 
            prev.includes(actionId) ? prev.filter(a => a !== actionId) : [...prev, actionId]
        );
    };

    const handleSavePermissions = async () => {
        setSaveLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/tenant/permissions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-Subdomain': tenantSubdomain
                },
                body: JSON.stringify({
                    role: selectedRole,
                    allowed_menus: allowedMenusList,
                    locked_actions: lockedActionsList
                })
            });
            const data = await response.json();
            if (data.success) {
                showToast(`Permissions updated successfully for ${selectedRole}!`);
                fetchPermissions();
            } else {
                showToast(data.error || 'Failed to save permissions');
            }
        } catch (err) {
            showToast('Network error while saving permissions');
        } finally {
            setSaveLoading(false);
        }
    };

    const MENU_OPTIONS = [
        { id: 'tables', label: 'Table Layout (टेबल लेआउट)' },
        { id: 'pos', label: 'Table POS Terminal (टेबल ऑर्डर POS)' },
        { id: 'quickPos', label: 'Quick Counter POS (काऊंटर ऑर्डर POS)' },
        { id: 'kds', label: 'KDS Monitor (किचन डिस्प्ले सिस्टीम)' },
        { id: 'captain', label: 'Captain Companion (कॅप्टन ऑर्डर ॲप)' },
        { id: 'reports', label: 'Sales Reports & Analytics (विक्री अहवाल)' },
        { id: 'liveStatus', label: 'Live Order Status (लाईव्ह ऑर्डर स्टेटस)' },
        { id: 'tokenDisplay', label: 'Token Display Screen (टोकन डिस्प्ले स्क्रीन)' },
        { id: 'qrOrdering', label: 'QR Customer Ordering (QR ग्राहक ऑर्डर)' },
        { id: 'printerSettings', label: 'Thermal Printer Config (प्रिंटर सेटिंग्ज)' }
    ];

    const ACTION_OPTIONS = [
        { id: 'apply_discount', label: 'Apply Discount (ऑर्डरवर डिस्काउंट देणे)' },
        { id: 'delete_order', label: 'Delete Order (तयार ऑर्डर रद्द करणे/डिलीट करणे)' },
        { id: 'edit_prices', label: 'Edit Menu Prices (मेन्यूचे दर बदलणे)' }
    ];

    return (
        <Box sx={{ p: 4, bgcolor: '#f9fafb', minHeight: 'calc(100vh - 70px)' }}>
            <Typography variant="h5" sx={{ fontWeight: 900, color: '#111827', mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <LockIcon sx={{ color: '#008751', fontSize: 32 }} />
                Role Permissions Config (वापरकर्ता रोल परवानग्या)
            </Typography>

            <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', mb: 2 }}>
                                Select User Role
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
                                Choose a staff role to configure allowed screen views and action restrictions.
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {['manager', 'captain', 'billing', 'kitchen'].map((role) => (
                                    <Button
                                        key={role}
                                        variant={selectedRole === role ? 'contained' : 'outlined'}
                                        onClick={() => setSelectedRole(role)}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            textTransform: 'capitalize',
                                            fontWeight: 800,
                                            py: 1.5,
                                            px: 2.5,
                                            borderRadius: 2.5,
                                            bgcolor: selectedRole === role ? '#008751' : 'transparent',
                                            color: selectedRole === role ? '#ffffff' : '#374151',
                                            borderColor: '#d1d5db',
                                            '&:hover': {
                                                bgcolor: selectedRole === role ? '#006f42' : '#f9fafb',
                                                borderColor: '#9ca3af'
                                            }
                                        }}
                                    >
                                        {role === 'billing' ? 'Billing Staff' : role === 'kitchen' ? 'Kitchen Staff' : role}
                                    </Button>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#111827', mb: 0.5, textTransform: 'capitalize' }}>
                                Permissions for: {selectedRole === 'billing' ? 'Billing Staff' : selectedRole === 'kitchen' ? 'Kitchen Staff' : selectedRole}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', mb: 4 }}>
                                Configure what menus this role is allowed to view in the sidebar and what actions are locked.
                            </Typography>

                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#374151', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                🖥️ Allowed Sidebar Menus (साईडबार पर्याय)
                            </Typography>
                            <Grid container spacing={1.5} sx={{ mb: 4 }}>
                                {MENU_OPTIONS.map((menu) => {
                                    const isChecked = allowedMenusList.includes(menu.id);
                                    return (
                                        <Grid item xs={12} sm={6} key={menu.id}>
                                            <Box 
                                                onClick={() => handleToggleMenu(menu.id)}
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    p: 1.5, 
                                                    border: '1px solid', 
                                                    borderColor: isChecked ? '#008751' : '#e5e7eb', 
                                                    borderRadius: 2,
                                                    bgcolor: isChecked ? '#f0fdf4' : '#ffffff',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        borderColor: '#008751',
                                                        bgcolor: '#f0fdf4'
                                                    }
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={isChecked}
                                                            color="primary"
                                                            sx={{
                                                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#008751' },
                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#008751' }
                                                            }}
                                                        />
                                                    }
                                                    label={<Typography variant="body2" sx={{ fontWeight: 700, color: '#374151' }}>{menu.label}</Typography>}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={() => handleToggleMenu(menu.id)}
                                                />
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            <Divider sx={{ my: 4 }} />

                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#374151', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                🔒 Locked Activities / Restrictions (प्रतिबंधित कृती)
                            </Typography>
                            <Grid container spacing={1.5} sx={{ mb: 4 }}>
                                {ACTION_OPTIONS.map((action) => {
                                    const isChecked = lockedActionsList.includes(action.id);
                                    return (
                                        <Grid item xs={12} key={action.id}>
                                            <Box 
                                                onClick={() => handleToggleAction(action.id)}
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    p: 1.5, 
                                                    border: '1px solid', 
                                                    borderColor: isChecked ? '#ef4444' : '#e5e7eb', 
                                                    borderRadius: 2,
                                                    bgcolor: isChecked ? '#fef2f2' : '#ffffff',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        borderColor: '#ef4444',
                                                        bgcolor: '#fef2f2'
                                                    }
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={isChecked}
                                                            color="error"
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 800, color: isChecked ? '#b91c1c' : '#374151' }}>
                                                                {action.label}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={() => handleToggleAction(action.id)}
                                                />
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSavePermissions}
                                    disabled={saveLoading}
                                    startIcon={<SaveIcon />}
                                    sx={{ 
                                        bgcolor: '#008751', 
                                        '&:hover': { bgcolor: '#006f42' }, 
                                        fontWeight: 800, 
                                        px: 4, 
                                        py: 1.2, 
                                        borderRadius: 2.5, 
                                        textTransform: 'none' 
                                    }}
                                >
                                    {saveLoading ? 'Saving...' : 'Save Permissions'}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}
